/**
 * 主题切换按钮组件
 * 提供亮色/暗色/跟随系统模式切换，可在前台和后台共用
 */

import React from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useVercelThemeOptional,
  type Theme,
} from "@/components/admin/VercelThemeProvider";
import { useTranslation } from "react-i18next";

type ResolvedTheme = "light" | "dark";

/**
 * 获取系统主题偏好
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * 获取当前主题
 */
function getCurrentTheme(): Theme {
  if (typeof window === "undefined") return "light";

  // 检查 localStorage
  const stored = localStorage.getItem("app-theme");
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }

  return "light";
}

/**
 * 应用主题到 DOM
 */
function applyTheme(resolved: ResolvedTheme, theme: Theme) {
  const root = document.documentElement;

  // 移除旧的主题类
  root.classList.remove("theme-vercel-light", "theme-vercel-dark", "dark");

  // 添加新的主题类
  root.classList.add(`theme-vercel-${resolved}`);

  // 暗色模式时同时添加 .dark 类
  if (resolved === "dark") {
    root.classList.add("dark");
  }

  // 保存到 localStorage
  localStorage.setItem("app-theme", theme);
}

export function ThemeToggle() {
  const { t } = useTranslation();
  // 使用可选的 context hook（不会抛出错误）
  const themeContext = useVercelThemeOptional();

  const [localTheme, setLocalTheme] = React.useState<Theme>(getCurrentTheme);
  const [localResolved, setLocalResolved] =
    React.useState<ResolvedTheme>(getSystemTheme);

  // 优先使用 context 中的主题，否则使用本地状态
  const theme = themeContext?.theme ?? localTheme;
  const resolvedTheme = themeContext?.resolvedTheme ?? localResolved;

  // 监听系统主题变化（仅在不使用 context 时）
  React.useEffect(() => {
    if (themeContext) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (localTheme === "system") {
        const newResolved = e.matches ? "dark" : "light";
        setLocalResolved(newResolved);
        applyTheme(newResolved, "system");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeContext, localTheme]);

  const toggleTheme = () => {
    let newTheme: Theme;
    if (theme === "light") {
      newTheme = "dark";
    } else if (theme === "dark") {
      newTheme = "system";
    } else {
      newTheme = "light";
    }

    if (themeContext) {
      // 使用 context 更新主题
      themeContext.setTheme(newTheme);
    } else {
      // 使用本地状态更新主题
      setLocalTheme(newTheme);
      const resolved = newTheme === "system" ? getSystemTheme() : newTheme;
      setLocalResolved(resolved);
      applyTheme(resolved, newTheme);
    }
  };

  // 初始化时应用主题（仅在不使用 context 时）
  React.useEffect(() => {
    if (!themeContext) {
      const resolved = localTheme === "system" ? getSystemTheme() : localTheme;
      setLocalResolved(resolved);
      applyTheme(resolved, localTheme);
    }
  }, []);

  const getTooltipText = () => {
    if (theme === "light") {
      return t("nav.switchToDark", "切换到暗色模式");
    } else if (theme === "dark") {
      return t("nav.switchToSystem", "切换到跟随系统");
    } else {
      return t("nav.switchToLight", "切换到亮色模式");
    }
  };

  const getIcon = () => {
    if (theme === "system") {
      return <Monitor className="h-4 w-4" />;
    } else if (resolvedTheme === "dark") {
      return <Sun className="h-4 w-4" />;
    } else {
      return <Moon className="h-4 w-4" />;
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label={getTooltipText()}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
        >
          {getIcon()}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{getTooltipText()}</TooltipContent>
    </Tooltip>
  );
}
